import { useSelector } from "react-redux";
import { AppState } from "types/AppState";
import { ModelVersion } from "components/EnhancedSpeech/useEnhanceSpeech/ModelVersion";
import { CurrentUser } from "reducers/CurrentUserReducer";
import { isClipWithAsset, ClipWithAsset } from "types/Clip";
import { Asset } from "types/Asset";

export const shouldUseStreamingPlayer = ({ player }: AppState): boolean =>
  player.streaming;

export const useStreamingPlayer = (): boolean =>
  useSelector(shouldUseStreamingPlayer);

export const shouldShowVideoPreview = ({ player }: AppState): boolean =>
  player.showVideoPreview;

export const useVideoPreviewEnabled = (): boolean =>
  useSelector(shouldShowVideoPreview);

export const selectActiveModelVersion = (state: AppState): ModelVersion => {
  const currentUser: CurrentUser | null = state?.currentUser ?? null;
  const projectModelVersion = state?.project?.enhance_speech_model_version;

  if (currentUser?.studioEnhanceSpeechV2Enabled === false) {
    return ModelVersion.v1;
  }

  return projectModelVersion ?? ModelVersion.v1;
};

/**
 * Selector to determine if the required outputs for the currently
 * active ES model version is missing across any project assets.
 */
export const isMissingActiveEnhanceSpeechModelOutputs = (
  state: AppState
): boolean => {
  const { assets, clips } = state;
  const modelVersion = selectActiveModelVersion(state);

  const streamingUrlField =
    modelVersion === ModelVersion.v1
      ? "streamingAudioV1Url"
      : "streamingAudioV2Url";

  const assetsWithUrls = clips
    .filter((clip): clip is ClipWithAsset => isClipWithAsset(clip))
    .map((clip) => assets[clip.assetId])
    .filter((asset): asset is Asset => asset !== undefined);

  const anyMissingEnhanceSpeechOutputs = assetsWithUrls.some((asset) => {
    if (!shouldUseStreamingPlayer(state) && modelVersion === ModelVersion.v1) {
      return (
        asset.studioSoundTrackId == null && asset[streamingUrlField] == null
      );
    } else {
      return asset[streamingUrlField] == null;
    }
  });

  return anyMissingEnhanceSpeechOutputs;
};
